import { useCallback, useEffect, useState } from 'react';
import { api, downloadFile, errorText, formatDate, formatQty, formatDateTime, oeffneBeleg } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';
import { Waage } from '../components/Waage';
import type { OutboundLine, OutboundOrder, PickTask } from '../types';

/**
 * Packtisch: die Arbeitsplatzansicht zwischen Kommissionierung und Verladung.
 * Bewusst eine eigene Seite mit grossen Flächen - hier steht jemand mit
 * Karton und Klebeband, nicht mit der Maus vor einer Tabelle.
 */
export function PackingPage() {
  const { can } = useAuth();
  const mayShip = can('admin', 'schichtleiter', 'lagerist', 'disponent');

  const [orders, setOrders] = useState<OutboundOrder[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<
    { order: OutboundOrder; lines: OutboundLine[]; tasks: PickTask[] } | null
  >(null);
  const [packing, setPacking] = useState({ packages: '1', weight: '', tracking: '', carrier: 'dhl' });
  const [versender, setVersender] = useState<{ code: string; name: string }[]>([]);
  // Läuft die Uhr, wird sie jede Sekunde neu gezeichnet. Der Wert selbst kommt
  // vom Server - die Uhr des Packtischrechners geht sonst irgendwann falsch.
  const [jetzt, setJetzt] = useState(() => Date.now());

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const all = await api<OutboundOrder[]>('/api/outbound/orders', { query: { openOnly: 'true' } });
      // Am Packtisch landen alle eingegangenen Aufträge, die noch nicht komplett
      // verladen sind - frische Anforderungen (noch nicht kommissioniert) ebenso
      // wie bereits gepickte. Nicht kommissionierte lassen sich am Packtisch mit
      // einem Klick kommissionieren und dann verladen.
      setOrders(all.filter((order) => order.qty_shipped < order.qty_ordered));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const loadDetail = useCallback(async (id: string) => {
    setDetail(null);
    try {
      setDetail(
        await api<{ order: OutboundOrder; lines: OutboundLine[]; tasks: PickTask[] }>(
          `/api/outbound/orders/${id}`,
        ),
      );
      setPacking({ packages: '1', weight: '', tracking: '', carrier: 'dhl' });
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    if (openId) void loadDetail(openId);
  }, [openId, loadDetail]);

  useEffect(() => {
    void api<{ code: string; name: string }[]>('/api/outbound/versender')
      .then(setVersender)
      .catch(() => setVersender([]));
  }, []);

  useEffect(() => {
    const uhr = window.setInterval(() => setJetzt(Date.now()), 1000);
    return () => window.clearInterval(uhr);
  }, []);

  const begonnen = detail?.order.packing_started_at ?? null;
  const laufzeit = begonnen ? Math.max(0, Math.floor((jetzt - new Date(begonnen).getTime()) / 1000)) : null;

  async function packenBeginnen() {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/outbound/orders/${detail.order.id}/packen-start`, { method: 'POST' });
      await loadDetail(detail.order.id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function kommissionieren() {
    if (!detail) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const r = await api<{ tasks: number; shortfalls: { sku: string; missing: number }[] }>(
        `/api/outbound/orders/${detail.order.id}/direkt-kommissionieren`,
        { method: 'POST' },
      );
      if (r.shortfalls.length > 0) {
        setError(
          'Nicht genug Bestand für: ' +
            r.shortfalls.map((s) => `${s.sku} (${s.missing})`).join(', ') +
            '. Der verfügbare Teil wurde kommissioniert.',
        );
      } else {
        setMessage('Kommissioniert – der Auftrag ist jetzt versandfertig.');
      }
      await loadDetail(detail.order.id);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function ship() {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/outbound/orders/${detail.order.id}/ship`, {
        method: 'POST',
        body: {
          packages: packing.packages ? Number(packing.packages) : null,
          weightKg: packing.weight ? Number(packing.weight.replace(',', '.')) : null,
          trackingRef: packing.tracking || null,
          carrier: packing.carrier || null,
        },
      });
      setMessage(`Auftrag ${detail.order.number} verladen. Lieferschein wird geladen …`);
      await downloadFile(`/api/printing/delivery-note/${detail.order.id}`);
      setOpenId(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function dhlLabel() {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      const r = await api<{ tracking: string; simulation: boolean }>(
        `/api/outbound/orders/${detail.order.id}/dhl-label`,
        {
          method: 'POST',
          body: {
            weightKg: packing.weight ? Number(packing.weight.replace(',', '.')) : null,
            packages: packing.packages ? Number(packing.packages) : null,
          },
        },
      );
      setPacking((p) => ({ ...p, tracking: r.tracking, carrier: 'dhl' }));
      setMessage(
        `DHL-Label erzeugt: ${r.tracking}${r.simulation ? ' (Simulation – kein echtes Label)' : ''}. Label wird geöffnet …`,
      );
      await oeffneBeleg(`/api/outbound/orders/${detail.order.id}/dhl-label.pdf`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function printNote(id: string) {
    setBusy(true);
    try {
      await downloadFile(`/api/printing/delivery-note/${id}`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const picked = detail?.tasks.filter((task) => task.qty_picked > 0) ?? [];
  const hazmat = picked.some((task) => task.hazmat);

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {!openId ? (
        <Card title="Bereit zum Packen" padded={false}>
          {!orders ? (
            <Loading />
          ) : orders.length === 0 ? (
            <Empty>
              Nichts zu packen. Sobald ein Auftrag eingeht, erscheint er hier.
            </Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Auftrag</th>
                  <th>Kunde</th>
                  <th>Eigentümer</th>
                  <th>Termin</th>
                  <th>Status</th>
                  <th className="num">Gepickt</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td className="mono">{order.number}</td>
                    <td>{order.customer_name ?? '—'}</td>
                    <td className="small">{order.owner_name}</td>
                    <td className="small">{formatDate(order.requested_at)}</td>
                    <td>
                      {order.qty_picked >= order.qty_ordered ? (
                        <Badge kind="ok">versandfertig</Badge>
                      ) : order.open_tasks > 0 ? (
                        <Badge kind="warn">{order.open_tasks} Picks offen</Badge>
                      ) : (
                        <Badge kind="info">neu</Badge>
                      )}
                    </td>
                    <td className="num">{formatQty(order.qty_picked)}</td>
                    <td className="num">
                      <button className="primary small" onClick={() => setOpenId(order.id)}>
                        an den Packtisch
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ) : !detail ? (
        <Loading />
      ) : (
        <>
          <Card
            title={`Packtisch — Auftrag ${detail.order.number}`}
            actions={
              <button className="small" onClick={() => setOpenId(null)}>
                zurück zur Liste
              </button>
            }
          >
            <div style={{ fontSize: 15 }}>
              <strong>{detail.order.customer_name ?? detail.order.owner_name}</strong>
              <div className="small muted">
                Ware im Eigentum von {detail.order.owner_name} · Lager {detail.order.warehouse_code}
                {detail.order.reference ? ` · Referenz ${detail.order.reference}` : ''}
              </div>
              {detail.order.fond_bezeichnung && (
                <div className="small">
                  <strong>Fond:</strong>{' '}
                  {detail.order.fond_kennung
                    ? `${detail.order.fond_kennung} — ${detail.order.fond_bezeichnung}`
                    : detail.order.fond_bezeichnung}
                </div>
              )}
              {(detail.order.deliver_street ||
                detail.order.deliver_company ||
                detail.order.deliver_last_name) && (
                <div className="small" style={{ marginTop: 4 }}>
                  <strong>Lieferanschrift:</strong>
                  <br />
                  {detail.order.deliver_company && (
                    <>
                      {detail.order.deliver_company}
                      <br />
                    </>
                  )}
                  {[detail.order.deliver_first_name, detail.order.deliver_last_name]
                    .filter(Boolean)
                    .join(' ')}
                  {(detail.order.deliver_first_name || detail.order.deliver_last_name) && <br />}
                  {detail.order.deliver_street && (
                    <>
                      {detail.order.deliver_street}
                      <br />
                    </>
                  )}
                  {`${detail.order.deliver_zip ?? ''} ${detail.order.deliver_city ?? ''}`.trim()}
                  {detail.order.deliver_info && (
                    <>
                      <br />
                      <span className="muted">{detail.order.deliver_info}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {hazmat && (
              <Notice kind="error">
                <strong>Gefahrgut in dieser Sendung.</strong> Kennzeichnung und Beförderungspapiere
                beachten.
              </Notice>
            )}

            {detail.order.qty_picked < detail.order.qty_ordered && (
              <Notice kind="info">
                Dieser Auftrag ist noch nicht (vollständig) kommissioniert. Mit einem Klick wird der
                verfügbare Bestand kommissioniert und der Auftrag versandfertig gemacht.
                <div style={{ marginTop: 8 }}>
                  <button className="primary" onClick={() => void kommissionieren()} disabled={busy}>
                    {busy ? 'Kommissioniere …' : 'Kommissionieren'}
                  </button>
                </div>
              </Notice>
            )}
          </Card>

          <Card title="Inhalt" padded={false}>
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Charge / MHD</th>
                  <th>Von Platz</th>
                  <th className="num">Menge</th>
                </tr>
              </thead>
              <tbody>
                {picked.map((task) => (
                  <tr key={task.id}>
                    <td style={{ fontSize: 14 }}>
                      <span className="mono">{task.sku}</span> {task.article_name}
                      {task.hazmat && (
                        <>
                          {' '}
                          <Badge kind="bad">Gefahrgut</Badge>
                        </>
                      )}
                    </td>
                    <td className="small">
                      {task.lot_code ?? '—'}
                      {task.best_before ? ` · ${formatDate(task.best_before)}` : ''}
                    </td>
                    <td className="mono small">{task.location_code}</td>
                    <td className="num" style={{ fontSize: 15, fontWeight: 600 }}>
                      {formatQty(task.qty_picked, task.unit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card title="Versandfertig machen">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                flexWrap: 'wrap',
                marginBottom: 16,
                paddingBottom: 14,
                borderBottom: '1px solid var(--line)',
              }}
            >
              {laufzeit === null ? (
                <>
                  <button onClick={packenBeginnen} disabled={busy}>
                    Packen beginnen
                  </button>
                  <span className="small muted">
                    Startet die Zeitnahme. Ohne sie lässt sich später nicht sagen, wie lange das
                    Packen wirklich gedauert hat — nur, wie viel Zeit seit dem letzten Griff ins
                    Regal vergangen ist.
                  </span>
                </>
              ) : (
                <>
                  <span
                    className="mono"
                    style={{ fontSize: 26, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {String(Math.floor(laufzeit / 60)).padStart(2, '0')}:
                    {String(laufzeit % 60).padStart(2, '0')}
                  </span>
                  <span className="small muted">
                    seit {formatDateTime(begonnen!)} am Packtisch
                    {laufzeit > 7200 && ' — läuft ungewöhnlich lange, bitte prüfen'}
                  </span>
                </>
              )}
            </div>

            <div className="form-grid">
              <TextField
                label="Anzahl Packstücke"
                value={packing.packages}
                onChange={(v) => setPacking({ ...packing, packages: v })}
              />
              <TextField
                label="Gewicht (kg)"
                value={packing.weight}
                onChange={(v) => setPacking({ ...packing, weight: v })}
              />
              <Waage onGewicht={(kg) => setPacking((p) => ({ ...p, weight: kg.toFixed(3).replace('.', ',') }))} />
              <label className="field">
                <span>Versender</span>
                <select
                  value={packing.carrier}
                  onChange={(e) => setPacking({ ...packing, carrier: e.target.value })}
                >
                  {versender.map((v) => (
                    <option key={v.code} value={v.code}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </label>
              <TextField
                label="Sendungsnummer (optional)"
                value={packing.tracking}
                onChange={(v) => setPacking({ ...packing, tracking: v })}
                hint="wird zum Verweis auf die Sendungsverfolgung"
              />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {mayShip && (
                <button onClick={dhlLabel} disabled={busy} title="DHL-Sendung anlegen und Label drucken">
                  DHL-Label erzeugen
                </button>
              )}
              {mayShip && (
                <button className="primary" onClick={ship} disabled={busy}>
                  {busy ? 'Wird gebucht …' : 'Verladen und Lieferschein drucken'}
                </button>
              )}
              <button onClick={() => printNote(detail.order.id)} disabled={busy}>
                Nur Lieferschein drucken
              </button>
            </div>
          </Card>
        </>
      )}
    </>
  );
}
